import { useMemo, useState } from "react";
import {
  Alert,
  App,
  Avatar,
  Button,
  Drawer,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import {
  channelScrapersApi,
  type BatchAction,
  type ChannelScraperConfig,
  type ChannelScraperRow,
  type ChannelScraperUpdateBody,
  type DryRunResult,
  type ScrapedSnapshot,
  type ScraperError,
  type ScraperRun,
  type ScraperType,
} from "../api/resources";
import { EmptyState, EnhancedTable, PageHeader } from "../components";

const SCRAPER_TYPE_OPTIONS: { value: ScraperType; label: string }[] = [
  { value: "manual", label: "manual（仅标记，不自动跑）" },
  { value: "mock", label: "mock（演示用，本地扰动）" },
  { value: "html", label: "html（网页抓取，需合规审查）" },
  { value: "json_api", label: "json_api（小程序/App 后端接口）" },
  { value: "affiliate_api", label: "affiliate_api（联盟 API）" },
];

const SCRAPER_TYPE_COLOR: Record<ScraperType, string> = {
  manual: "default",
  mock: "blue",
  html: "orange",
  json_api: "purple",
  affiliate_api: "green",
};

const CRON_TOOLTIP =
  'cron 表达式：分 时 日 月 周。例：\n"0 */6 * * *"  每 6 小时\n"30 3 * * *"   每天 03:30\n"*/15 * * * *" 每 15 分钟';

const HTML_CONFIG_PLACEHOLDER = `{
  "selectors": {
    "finalPrice": ".price.now",
    "listPrice": ".price.was"
  },
  "stockMap": {
    "现货": "in_stock",
    "缺货": "out_of_stock"
  }
}`;

type ScraperState = "未配置" | "已暂停" | "失败" | "已启用" | "已停用";

/** Derived row state used by status column + per-row action menu visibility. */
function deriveState(scraper: ChannelScraperConfig | null): ScraperState {
  if (!scraper) return "未配置";
  if (scraper.pausedAt) return "已暂停";
  if (scraper.lastRunStatus === "failed") return "失败";
  return scraper.enabled ? "已启用" : "已停用";
}

function formatDuration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return "—";
  const ms = dayjs(finishedAt).diff(dayjs(startedAt));
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m${s % 60}s`;
}

function renderLastRunStatus(scraper: ChannelScraperConfig | null) {
  if (!scraper || !scraper.lastRunAt) return <Typography.Text type="secondary">尚未运行</Typography.Text>;
  const color =
    scraper.lastRunStatus === "success"
      ? "green"
      : scraper.lastRunStatus === "failed"
        ? "red"
        : "default";
  const tag = <Tag color={color}>{scraper.lastRunStatus ?? "—"}</Tag>;
  const time = dayjs(scraper.lastRunAt).format("YYYY-MM-DD HH:mm");
  const node = (
    <Space size={6}>
      {tag}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {time}
      </Typography.Text>
    </Space>
  );
  if (scraper.lastRunStatus === "failed" && scraper.lastError) {
    return <Tooltip title={scraper.lastError}>{node}</Tooltip>;
  }
  return node;
}

export function ChannelScrapersPage() {
  const qc = useQueryClient();
  const { message, modal } = App.useApp();

  const [editing, setEditing] = useState<ChannelScraperRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  type DrawerFormValues = {
    scraperType: ScraperType;
    enabled: boolean;
    scheduleCron: string;
    configJsonText: string;
    maxConcurrentFetches: number;
    alertAfterFailures: number;
    retryOnFailure: boolean;
  };
  const [form] = Form.useForm<DrawerFormValues>();
  const [configJsonError, setConfigJsonError] = useState<string | null>(null);

  const [historyOpenFor, setHistoryOpenFor] = useState<ChannelScraperRow | null>(null);
  const [historyPage, setHistoryPage] = useState(1);

  const [dryRunOpen, setDryRunOpen] = useState(false);
  const [dryRunData, setDryRunData] = useState<DryRunResult | null>(null);
  const [dryRunFor, setDryRunFor] = useState<ChannelScraperRow | null>(null);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const list = useQuery({
    queryKey: ["channel-scrapers"],
    queryFn: () => channelScrapersApi.list(),
  });

  const runsQuery = useQuery({
    queryKey: ["channel-scraper-runs", historyOpenFor?.channel.id, historyPage],
    queryFn: () =>
      channelScrapersApi.listRuns(historyOpenFor!.channel.id, {
        page: historyPage,
        pageSize: 20,
      }),
    enabled: !!historyOpenFor,
  });

  const saveMutation = useMutation({
    mutationFn: ({ channelId, body }: { channelId: string; body: ChannelScraperUpdateBody }) =>
      channelScrapersApi.update(channelId, body),
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["channel-scrapers"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ row, enabled }: { row: ChannelScraperRow; enabled: boolean }) => {
      // 行内 Switch：依赖现有 scraper 配置，未配置则不允许直接开启
      const s = row.scraper;
      if (!s) {
        return Promise.reject(new Error("请先点击编辑配置 scraper 类型"));
      }
      const body: ChannelScraperUpdateBody = {
        scraperType: s.scraperType,
        enabled,
        scheduleCron: s.scheduleCron,
        configJson: s.configJson,
      };
      return channelScrapersApi.update(row.channel.id, body);
    },
    onSuccess: (_, vars) => {
      message.success(vars.enabled ? "已启用" : "已停用");
      qc.invalidateQueries({ queryKey: ["channel-scrapers"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const runNowMutation = useMutation({
    mutationFn: (channelId: string) => channelScrapersApi.runNow(channelId),
    onError: (err: Error) => message.error(err.message),
  });

  const dryRunMutation = useMutation({
    mutationFn: (channelId: string) => channelScrapersApi.dryRun(channelId),
    onError: (err: Error) => message.error(err.message),
  });

  const resumeMutation = useMutation({
    mutationFn: (channelId: string) => channelScrapersApi.resume(channelId),
    onSuccess: () => {
      message.success("已恢复");
      qc.invalidateQueries({ queryKey: ["channel-scrapers"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const batchMutation = useMutation({
    mutationFn: (body: { ids: string[]; action: BatchAction; reason?: string }) =>
      channelScrapersApi.batch(body),
    onSuccess: (data) => {
      message.success(`批量 ${data.action} 完成，影响 ${data.changed} 条`);
      qc.invalidateQueries({ queryKey: ["channel-scrapers"] });
      setSelectedKeys([]);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const openDrawer = (row: ChannelScraperRow) => {
    setEditing(row);
    setConfigJsonError(null);
    const s = row.scraper;
    form.resetFields();
    form.setFieldsValue({
      scraperType: s?.scraperType ?? "manual",
      enabled: s?.enabled ?? false,
      scheduleCron: s?.scheduleCron ?? "0 */6 * * *",
      configJsonText: JSON.stringify(s?.configJson ?? {}, null, 2),
      maxConcurrentFetches: s?.maxConcurrentFetches ?? 5,
      alertAfterFailures: s?.alertAfterFailures ?? 3,
      retryOnFailure: s?.retryOnFailure ?? false,
    });
    setDrawerOpen(true);
  };

  const openHistory = (row: ChannelScraperRow) => {
    setHistoryOpenFor(row);
    setHistoryPage(1);
  };

  const triggerRunNow = (row: ChannelScraperRow) => {
    runNowMutation.mutate(row.channel.id, {
      onSuccess: (data) => {
        modal.success({
          title: "已触发运行",
          content: (
            <Space direction="vertical" size={4}>
              <Typography.Text>
                runId：<Typography.Text code>{data.runId}</Typography.Text>
              </Typography.Text>
              <Typography.Text>
                当前状态：<Tag>{data.status}</Tag>
              </Typography.Text>
              <Typography.Text type="secondary">
                可在“运行历史”查看详情
              </Typography.Text>
            </Space>
          ),
          okText: "查看运行历史",
          onOk: () => openHistory(row),
        });
        qc.invalidateQueries({ queryKey: ["channel-scrapers"] });
      },
    });
  };

  const triggerDryRun = (row: ChannelScraperRow) => {
    setDryRunFor(row);
    setDryRunData(null);
    setDryRunOpen(true);
    dryRunMutation.mutate(row.channel.id, {
      onSuccess: (data) => setDryRunData(data),
    });
  };

  const handleSave = async () => {
    let values: DrawerFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    let configJson: Record<string, unknown>;
    try {
      const parsed = JSON.parse(values.configJsonText || "{}");
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("configJson 必须是 JSON 对象");
      }
      configJson = parsed as Record<string, unknown>;
      setConfigJsonError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setConfigJsonError(msg);
      message.error(`configJson JSON 解析失败：${msg}`);
      return;
    }

    if (!editing) return;
    const body: ChannelScraperUpdateBody = {
      scraperType: values.scraperType,
      enabled: values.enabled,
      scheduleCron: values.scheduleCron.trim(),
      configJson,
      maxConcurrentFetches: values.maxConcurrentFetches,
      alertAfterFailures: values.alertAfterFailures,
      retryOnFailure: values.retryOnFailure,
    };

    const doSave = () => saveMutation.mutate({ channelId: editing.channel.id, body });

    // enabled=true 时给二次确认（防误开，尤其是 html 类型）
    if (body.enabled) {
      modal.confirm({
        title: "确认启用此渠道的自动采集？",
        content:
          body.scraperType === "html"
            ? "你正在启用 HTML 爬虫，请确保已确认目标网站的 robots.txt 与服务条款，并已取得抓取许可。"
            : `将以 cron 表达式 "${body.scheduleCron}" 周期性触发 ${body.scraperType} 采集。`,
        okText: "确认启用",
        cancelText: "取消",
        okButtonProps: body.scraperType === "html" ? { danger: true } : undefined,
        onOk: doSave,
      });
    } else {
      doSave();
    }
  };

  const watchedType = Form.useWatch("scraperType", form) as ScraperType | undefined;

  const dataSource = list.data?.items ?? [];
  const rows = useMemo(() => dataSource, [dataSource]);

  // Batch operations require at least one selected row with a scraper config.
  const selectedRows = rows.filter((r) => selectedKeys.includes(r.channel.id) && r.scraper);
  const selectedScraperIds = selectedRows.map((r) => r.scraper!.id);

  const triggerBatch = (action: BatchAction) => {
    if (selectedScraperIds.length === 0) {
      message.warning("请先勾选至少一行（含已配置的 scraper）");
      return;
    }
    modal.confirm({
      title: `确认批量 ${action}？`,
      content: `将影响 ${selectedScraperIds.length} 条已配置的 scraper。`,
      okText: "确认",
      okButtonProps: action === "disable" || action === "pause" ? { danger: true } : undefined,
      onOk: () => batchMutation.mutate({ ids: selectedScraperIds, action }),
    });
  };

  const renderStatusCell = (row: ChannelScraperRow) => {
    const state = deriveState(row.scraper);
    const s = row.scraper;
    if (state === "已暂停" && s) {
      const tip = s.pausedReason
        ? `已于 ${dayjs(s.pausedAt!).format("YYYY-MM-DD HH:mm")} 暂停：${s.pausedReason}`
        : `已于 ${dayjs(s.pausedAt!).format("YYYY-MM-DD HH:mm")} 暂停`;
      return (
        <Tooltip title={tip}>
          <Tag color="orange">已暂停</Tag>
        </Tooltip>
      );
    }
    if (state === "失败") return <Tag color="red">失败</Tag>;
    if (state === "已启用") return <Tag color="green">已启用</Tag>;
    if (state === "已停用") return <Tag>已停用</Tag>;
    return <Tag>未配置</Tag>;
  };

  return (
    <div>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="合规提示"
        description={
          <span>
            启用 HTML 爬虫前请确认目标网站的 robots.txt 与服务条款；未取得许可的抓取可能违反《反不正当竞争法》《数据安全法》。Mock / Affiliate API 类型不涉及此风险。
          </span>
        }
      />

      <PageHeader
        title="采集管理"
        description="配置各渠道的采集类型、调度与启停。后台 cron 每 6 小时自动运行启用中的任务。"
        stats={[{ label: `共 ${rows.length} 个渠道` }]}
        extra={
          <Tooltip title="后台 cron 每 6 小时自动跑一次启用中的采集任务">
            <Button onClick={() => list.refetch()} loading={list.isFetching}>
              刷新
            </Button>
          </Tooltip>
        }
      />

      <Space style={{ marginBottom: 12 }} wrap>
        <Typography.Text type="secondary">
          已选 {selectedScraperIds.length} 条：
        </Typography.Text>
        <Button
          disabled={selectedScraperIds.length === 0}
          loading={batchMutation.isPending}
          onClick={() => triggerBatch("enable")}
        >
          批量启用
        </Button>
        <Button
          disabled={selectedScraperIds.length === 0}
          loading={batchMutation.isPending}
          onClick={() => triggerBatch("disable")}
        >
          批量停用
        </Button>
        <Button
          disabled={selectedScraperIds.length === 0}
          loading={batchMutation.isPending}
          onClick={() => triggerBatch("reset-failures")}
        >
          批量重置失败计数
        </Button>
      </Space>

      {!list.isLoading && rows.length === 0 ? (
        <EmptyState
          title="暂无可采集的渠道"
          description="尚未有任何渠道，请先到「渠道管理」创建渠道。"
        />
      ) : (
      <EnhancedTable<ChannelScraperRow>
        rowKey={(row) => row.channel.id}
        loading={list.isLoading}
        dataSource={rows}
        persistKey="channel-scrapers"
        exportFilename="channel-scrapers"
        pagination={false}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys) => setSelectedKeys(keys as string[]),
          getCheckboxProps: (row) => ({ disabled: !row.scraper }),
        }}
        columns={[
          {
            title: "渠道",
            dataIndex: ["channel", "name"],
            render: (_, row) => (
              <Space>
                {row.channel.logoUrl ? (
                  <Avatar src={row.channel.logoUrl} size="small" shape="square" />
                ) : (
                  <Avatar size="small" shape="square">
                    {row.channel.name.slice(0, 1)}
                  </Avatar>
                )}
                <span>{row.channel.name}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {row.channel.code}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: "采集类型",
            width: 140,
            render: (_, row) => {
              if (!row.scraper) {
                return <Typography.Text type="secondary">未配置</Typography.Text>;
              }
              return (
                <Tag color={SCRAPER_TYPE_COLOR[row.scraper.scraperType]}>
                  {row.scraper.scraperType}
                </Tag>
              );
            },
          },
          {
            title: "状态",
            width: 110,
            render: (_, row) => renderStatusCell(row),
          },
          {
            title: "启用",
            width: 70,
            render: (_, row) => (
              <Switch
                checked={!!row.scraper?.enabled}
                disabled={!row.scraper || toggleEnabledMutation.isPending}
                onChange={(checked) => {
                  if (!row.scraper) return;
                  if (checked) {
                    modal.confirm({
                      title: "确认启用此渠道的自动采集？",
                      content:
                        row.scraper.scraperType === "html"
                          ? "你正在启用 HTML 爬虫，请确保已确认目标网站的 robots.txt 与服务条款。"
                          : `将以 cron 表达式 "${row.scraper.scheduleCron}" 周期性触发。`,
                      okText: "确认启用",
                      cancelText: "取消",
                      okButtonProps:
                        row.scraper.scraperType === "html" ? { danger: true } : undefined,
                      onOk: () => toggleEnabledMutation.mutate({ row, enabled: true }),
                    });
                  } else {
                    toggleEnabledMutation.mutate({ row, enabled: false });
                  }
                }}
              />
            ),
          },
          {
            title: "连续失败",
            width: 90,
            align: "right",
            render: (_, row) => {
              const n = row.scraper?.consecutiveFailures ?? 0;
              if (!row.scraper) return <Typography.Text type="secondary">—</Typography.Text>;
              if (n > 0) {
                const alertAt = row.scraper.alertAfterFailures;
                return (
                  <Tooltip title={`alertAfterFailures = ${alertAt}（达到后自动暂停）`}>
                    <Typography.Text type="danger" strong>{n}</Typography.Text>
                  </Tooltip>
                );
              }
              return <Typography.Text type="secondary">0</Typography.Text>;
            },
          },
          {
            title: "调度",
            width: 130,
            render: (_, row) =>
              row.scraper ? (
                <Typography.Text code style={{ fontFamily: "monospace" }}>
                  {row.scraper.scheduleCron}
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: "上次运行",
            width: 200,
            render: (_, row) => renderLastRunStatus(row.scraper),
          },
          {
            title: "操作",
            width: 220,
            render: (_, row) => {
              const state = deriveState(row.scraper);
              const items = [
                {
                  key: "dry-run",
                  label: "Dry-run（试跑）",
                  disabled: !row.scraper || row.scraper.scraperType === "manual",
                  onClick: () => triggerDryRun(row),
                },
                ...(state === "已暂停"
                  ? [
                      {
                        key: "resume",
                        label: "Resume（恢复）",
                        onClick: () =>
                          modal.confirm({
                            title: "确认恢复此渠道的采集？",
                            content: "将清除暂停状态并把连续失败计数归零。",
                            okText: "恢复",
                            onOk: () => resumeMutation.mutate(row.channel.id),
                          }),
                      },
                    ]
                  : [
                      {
                        key: "pause",
                        label: "Pause（暂停）",
                        disabled: !row.scraper,
                        onClick: () =>
                          modal.confirm({
                            title: "确认暂停此渠道的采集？",
                            content: "暂停后 cron + run-now 都将跳过此 scraper，直到手动恢复。",
                            okText: "暂停",
                            okButtonProps: { danger: true },
                            onOk: () =>
                              batchMutation.mutate({
                                ids: [row.scraper!.id],
                                action: "pause",
                              }),
                          }),
                      },
                    ]),
                ...(state === "失败" || (row.scraper?.consecutiveFailures ?? 0) > 0
                  ? [
                      {
                        key: "reset",
                        label: "Reset failures",
                        onClick: () =>
                          batchMutation.mutate({
                            ids: [row.scraper!.id],
                            action: "reset-failures",
                          }),
                      },
                    ]
                  : []),
              ];
              return (
                <Space size={0}>
                  <Button type="link" onClick={() => openDrawer(row)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="立即运行一次？"
                    description={
                      row.scraper?.scraperType === "html"
                        ? "HTML 抓取将访问目标网站，请确认合规后继续。"
                        : "将忽略调度立即触发一次采集任务。"
                    }
                    okText="运行"
                    cancelText="取消"
                    disabled={!row.scraper || !!row.scraper.pausedAt}
                    onConfirm={() => triggerRunNow(row)}
                  >
                    <Button
                      type="link"
                      disabled={!row.scraper || !!row.scraper.pausedAt}
                      loading={
                        runNowMutation.isPending &&
                        runNowMutation.variables === row.channel.id
                      }
                    >
                      立即运行
                    </Button>
                  </Popconfirm>
                  <Button type="link" onClick={() => openHistory(row)} disabled={!row.scraper}>
                    历史
                  </Button>
                  <Dropdown
                    menu={{ items }}
                    disabled={!row.scraper}
                    trigger={["click"]}
                  >
                    <Button type="link">更多</Button>
                  </Dropdown>
                </Space>
              );
            },
          },
        ]}
      />
      )}

      <Drawer
        open={drawerOpen}
        title={editing ? `编辑采集 - ${editing.channel.name}` : "编辑采集"}
        width={560}
        destroyOnClose
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
          setConfigJsonError(null);
        }}
        extra={
          <Space>
            <Button
              onClick={() => {
                setDrawerOpen(false);
                setEditing(null);
                setConfigJsonError(null);
              }}
            >
              取消
            </Button>
            <Button type="primary" loading={saveMutation.isPending} onClick={handleSave}>
              保存
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="scraperType"
            label="采集类型"
            rules={[{ required: true, message: "请选择采集类型" }]}
          >
            <Select options={SCRAPER_TYPE_OPTIONS} />
          </Form.Item>

          {watchedType === "manual" && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="manual 类型说明"
              description="本类型不会自动运行，仅作为标记；该渠道的价格请通过 CSV 上传维护。"
            />
          )}
          {watchedType === "mock" && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="mock 类型说明"
              description="演示用：自动在现价基础上生成 ±5% 扰动价格，不发出任何外网请求。"
            />
          )}
          {watchedType === "html" && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="HTML 爬虫合规提醒"
              description={
                <Space direction="vertical" size={4}>
                  <span>
                    在启用前，请先确认目标网站的 robots.txt 与服务条款，并取得抓取许可。未授权抓取可能产生法律责任。
                  </span>
                  <span>configJson 形状示例：</span>
                  <Typography.Text code style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                    {HTML_CONFIG_PLACEHOLDER}
                  </Typography.Text>
                </Space>
              }
            />
          )}
          {watchedType === "affiliate_api" && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="affiliate_api 类型说明"
              description="将使用此渠道在“渠道联盟配置”中已配置的 PID / 凭证调用联盟 API 拉取价格。"
            />
          )}

          <Form.Item name="enabled" label="启用自动运行" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="scheduleCron"
            label={
              <Space size={4}>
                <span>调度 cron</span>
                <Tooltip title={<pre style={{ margin: 0 }}>{CRON_TOOLTIP}</pre>}>
                  <Typography.Text type="secondary" style={{ cursor: "help" }}>
                    （?）
                  </Typography.Text>
                </Tooltip>
              </Space>
            }
            extra="5 字段 cron：分 时 日 月 周"
            rules={[
              { required: true, message: "请输入 cron 表达式" },
              {
                validator: (_, v: string) => {
                  if (!v) return Promise.resolve();
                  const parts = v.trim().split(/\s+/);
                  if (parts.length !== 5)
                    return Promise.reject(new Error("cron 必须是 5 段（分 时 日 月 周）"));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input style={{ fontFamily: "monospace" }} placeholder="0 */6 * * *" />
          </Form.Item>

          <Form.Item
            name="maxConcurrentFetches"
            label="最大并发抓取数 (maxConcurrentFetches)"
            extra="HtmlScraper 并发批量大小，默认 5。"
            rules={[{ required: true, type: "number", min: 1, max: 50 }]}
          >
            <InputNumber min={1} max={50} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="alertAfterFailures"
            label="连续失败告警阈值 (alertAfterFailures)"
            extra="连续失败达到该数值后自动暂停并发出错误日志，默认 3。"
            rules={[{ required: true, type: "number", min: 1, max: 99 }]}
          >
            <InputNumber min={1} max={99} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="retryOnFailure"
            label="单次失败后立即重试一次 (retryOnFailure)"
            valuePropName="checked"
            extra="开启后单次失败会就地重试一次，仍失败才计入连续失败计数。"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            label={
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <span>configJson</span>
                <Button
                  size="small"
                  onClick={() => {
                    const text = form.getFieldValue("configJsonText") as string | undefined;
                    try {
                      const parsed = JSON.parse(text || "{}");
                      form.setFieldValue(
                        "configJsonText",
                        JSON.stringify(parsed, null, 2),
                      );
                      setConfigJsonError(null);
                    } catch (err) {
                      const msg = err instanceof Error ? err.message : String(err);
                      setConfigJsonError(msg);
                      message.error(`JSON 解析失败：${msg}`);
                    }
                  }}
                >
                  格式化
                </Button>
              </Space>
            }
            required
            validateStatus={configJsonError ? "error" : undefined}
            help={configJsonError ?? undefined}
          >
            <Form.Item name="configJsonText" noStyle>
              <Input.TextArea
                rows={10}
                style={{ fontFamily: "monospace", fontSize: 12 }}
                placeholder={HTML_CONFIG_PLACEHOLDER}
                onBlur={(e) => {
                  const text = e.target.value;
                  if (!text || !text.trim()) {
                    setConfigJsonError(null);
                    return;
                  }
                  try {
                    const parsed = JSON.parse(text);
                    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
                      throw new Error("必须是 JSON 对象");
                    }
                    setConfigJsonError(null);
                  } catch (err) {
                    setConfigJsonError(err instanceof Error ? err.message : String(err));
                  }
                }}
              />
            </Form.Item>
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        open={!!historyOpenFor}
        title={historyOpenFor ? `运行历史 - ${historyOpenFor.channel.name}` : "运行历史"}
        width={1000}
        footer={null}
        onCancel={() => {
          setHistoryOpenFor(null);
          setHistoryPage(1);
        }}
      >
        <Table<ScraperRun>
          rowKey="id"
          size="small"
          loading={runsQuery.isLoading || runsQuery.isFetching}
          dataSource={runsQuery.data?.items ?? []}
          pagination={{
            current: historyPage,
            pageSize: 20,
            total: runsQuery.data?.total ?? 0,
            onChange: (p) => setHistoryPage(p),
            showSizeChanger: false,
          }}
          expandable={{
            expandedRowRender: (row) =>
              row.errorMessage ? (
                <Typography.Paragraph
                  type="danger"
                  style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                >
                  {row.errorMessage}
                </Typography.Paragraph>
              ) : (
                <Typography.Text type="secondary">无错误信息</Typography.Text>
              ),
            rowExpandable: (row) => !!row.errorMessage,
          }}
          columns={[
            {
              title: "开始时间",
              dataIndex: "startedAt",
              width: 160,
              render: (v) => dayjs(v).format("YYYY-MM-DD HH:mm:ss"),
            },
            {
              title: "结束时间",
              dataIndex: "finishedAt",
              width: 160,
              render: (v: string | null) =>
                v ? dayjs(v).format("YYYY-MM-DD HH:mm:ss") : "—",
            },
            {
              title: "时长",
              width: 80,
              render: (_, row) => {
                if (row.durationMs != null) {
                  if (row.durationMs < 1000) return `${row.durationMs}ms`;
                  return `${(row.durationMs / 1000).toFixed(1)}s`;
                }
                return formatDuration(row.startedAt, row.finishedAt);
              },
            },
            {
              title: "状态",
              dataIndex: "status",
              width: 90,
              render: (v: ScraperRun["status"]) => {
                const color =
                  v === "success" ? "green" : v === "failed" ? "red" : "blue";
                return <Tag color={color}>{v}</Tag>;
              },
            },
            {
              title: "Dry-run",
              dataIndex: "dryRun",
              width: 80,
              render: (v: boolean) =>
                v ? <Tag color="purple">dry-run</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
            },
            {
              title: "插入",
              dataIndex: "insertedCount",
              width: 70,
              align: "right",
            },
            {
              title: "跳过",
              dataIndex: "skippedCount",
              width: 70,
              align: "right",
            },
            {
              title: "价差条数",
              dataIndex: "priceChangesCount",
              width: 90,
              align: "right",
              render: (v: number) => (
                <Tooltip title="本次抓到的 snapshots 中 finalPrice 与上次最新快照不同的条数">
                  <span>{v}</span>
                </Tooltip>
              ),
            },
            {
              title: "触发",
              dataIndex: "triggeredBy",
              width: 130,
              render: (v: string) => {
                if (v === "cron") return <Tag>cron</Tag>;
                if (v.startsWith("manual:")) {
                  return (
                    <Tooltip title={v}>
                      <Tag color="purple">manual</Tag>
                    </Tooltip>
                  );
                }
                if (v.startsWith("dry-run:")) {
                  return (
                    <Tooltip title={v}>
                      <Tag color="cyan">dry-run</Tag>
                    </Tooltip>
                  );
                }
                return <Tag>{v}</Tag>;
              },
            },
            {
              title: "错误",
              dataIndex: "errorMessage",
              ellipsis: true,
              render: (v: string | null) =>
                v ? (
                  <Tooltip title={v}>
                    <Typography.Text type="danger" ellipsis style={{ maxWidth: 200 }}>
                      {v}
                    </Typography.Text>
                  </Tooltip>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
            },
          ]}
        />
      </Modal>

      <Modal
        open={dryRunOpen}
        title={dryRunFor ? `Dry-run 结果 - ${dryRunFor.channel.name}` : "Dry-run 结果"}
        width={900}
        footer={[
          <Button key="close" onClick={() => setDryRunOpen(false)}>
            关闭
          </Button>,
        ]}
        onCancel={() => setDryRunOpen(false)}
      >
        {dryRunMutation.isPending ? (
          <Typography.Text type="secondary">正在执行 dry-run…</Typography.Text>
        ) : !dryRunData ? (
          <Typography.Text type="secondary">无结果</Typography.Text>
        ) : (
          <DryRunResultView data={dryRunData} />
        )}
      </Modal>
    </div>
  );
}

function DryRunResultView({ data }: { data: DryRunResult }) {
  const statusColor =
    data.status === "success" ? "green" : data.status === "failed" ? "red" : "blue";
  const previewSnapshots = data.snapshots.slice(0, 20);
  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Space size={16} wrap>
        <span>
          状态：<Tag color={statusColor}>{data.status}</Tag>
        </span>
        <span>
          runId：<Typography.Text code>{data.runId}</Typography.Text>
        </span>
        <span>
          时长：
          {data.durationMs != null
            ? data.durationMs < 1000
              ? `${data.durationMs}ms`
              : `${(data.durationMs / 1000).toFixed(1)}s`
            : "—"}
        </span>
        <span>共 {data.snapshotCountTotal} 条 snapshots</span>
        <span>价差 {data.priceChanges} 条</span>
        <span>错误 {data.errors.length} 条</span>
      </Space>

      <Alert
        type="info"
        showIcon
        message="dry-run 不会写入 price_snapshots —— 用于验证选择器或 API 配置是否正常。"
      />

      <Typography.Text strong>
        Snapshots（预览前 20 / 共 {data.snapshotCountTotal} 条）
      </Typography.Text>
      <Table<ScrapedSnapshot>
        rowKey={(r, idx) => `${r.channelProductId}-${idx ?? 0}`}
        size="small"
        pagination={false}
        dataSource={previewSnapshots}
        locale={{ emptyText: "未抓取到任何 snapshot" }}
        columns={[
          { title: "channelProductId", dataIndex: "channelProductId" },
          {
            title: "finalPrice",
            dataIndex: "finalPrice",
            width: 110,
            align: "right",
            render: (v: number) => v.toFixed(2),
          },
          {
            title: "listPrice",
            dataIndex: "listPrice",
            width: 110,
            align: "right",
            render: (v: number | null) => (v == null ? "—" : v.toFixed(2)),
          },
          {
            title: "stockStatus",
            dataIndex: "stockStatus",
            width: 110,
          },
        ]}
      />

      {data.errors.length > 0 && (
        <>
          <Typography.Text strong type="danger">
            错误（{data.errors.length}）
          </Typography.Text>
          <Table<ScraperError>
            rowKey={(r, idx) => `${r.channelProductId ?? "_"}-${idx ?? 0}`}
            size="small"
            pagination={false}
            dataSource={data.errors}
            columns={[
              {
                title: "channelProductId",
                dataIndex: "channelProductId",
                width: 200,
                render: (v: string | undefined) => v ?? "—",
              },
              { title: "reason", dataIndex: "reason" },
            ]}
          />
        </>
      )}
    </Space>
  );
}
